import {
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import {
  AutocompleteFilterModel,
  AutocompleteFilterOption,
  DatasetID,
  FilterType,
} from '../../models/filter.model';
import { useEffect, useRef, useState } from 'react';
import { useGetFilterValuesQuery } from '../../store/api/filters.api';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  removeCommonFilter,
  selectCommonFilters,
  updateCommonFilter as updateCommonFiltersAction,
} from '../../store/slices/common-filters.slice';
import {
  selectTempEndDate,
  selectTempStartDate,
  selectTempVisualData,
  selectTempVisualType,
} from '../../store/slices/date-filter.slice';
import { selectDatasetQuestion } from '../../store/slices/dataset.slice';
import { RootState } from '../../../common/store';
import { selectFilterParent } from '../../store/slices/filter-parent.slice';
import useClaims from 'common/hooks/useClaims';
import { Claim } from 'common/models/page-config.models';
import { formatElementId } from '../widgets/utils';
import useFilterValidation from 'common/hooks/useFilterValidation';
import { DEFAULT_END_DATE, DEFAULT_FILTER_VALIDATION_MESSAGE } from 'common/constants';
import { useChartFiltersAsPageFilters } from 'common/hooks/filters';
import { useSelector } from 'react-redux';
import { removeChartFilter, selectChartFilters } from 'common/store/slices/chart-filters.slice';
import { adjustFilterDates } from 'common/hooks/utils';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { useParams } from 'react-router-dom';

export function AutocompleteFilter({
  label,
  property,
  datasetId,
  overrideParentFilter,
  requiredFilters,
  validationMessage,
}: AutocompleteFilterModel & DatasetID) {
  const [data, setData] = useState<any>(undefined);
  const { hasClaim } = useClaims();
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state: RootState) => selectCommonFilters(state, datasetId));
  const { projectId } = useParams();
  const question = useAppSelector((state: RootState) =>
    selectDatasetQuestion(state, datasetId, projectId ?? '')
  );
  const filter = filters?.[property];
  const filterValue = filter?.value ?? [];
  const chartFilters = useSelector((state: RootState) => selectChartFilters(state, datasetId));
  const convertedChartFilters = useChartFiltersAsPageFilters(datasetId);
  const chartFilterValue = convertedChartFilters[property]?.value;
  const popupContainerRef = useRef<HTMLDivElement | null>(null);
  const tempStartDate = useAppSelector((state: RootState) => selectTempStartDate(state, datasetId));
  const visualData = useAppSelector((state: RootState) => selectTempVisualData(state, datasetId));
  const visualType = useAppSelector((state: RootState) => selectTempVisualType(state, datasetId));
  const tempEndDate =
    useAppSelector((state: RootState) => selectTempEndDate(state, datasetId)) ?? DEFAULT_END_DATE;
  const filterParent = useAppSelector(selectFilterParent);
  const { activeProject } = useModuleProjectContext();

  const filterValid = useFilterValidation({ datasetId, filters: requiredFilters });

  const { startDate, endDate } = adjustFilterDates({
    startDate: tempStartDate,
    endDate: tempEndDate,
    visualData,
    visualType,
  });

  const {
    data: fullData,
    error,
    isLoading,
    isFetching,
  } = useGetFilterValuesQuery(
    {
      filterString: property,
      startDate,
      endDate,
      questionId: question?.id ?? -1,
      filterParent: overrideParentFilter ?? filterParent,
      includeAlignment: hasClaim(Claim.Alignment),
    },
    {
      skip: !activeProject,
      refetchOnMountOrArgChange: true,
    }
  );

  const dropdownData = error || !data ? [] : data;

  const composedFilterValue = [...(filterValue ?? []), ...(chartFilterValue ?? [])].filter(
    (item, index, self) => self.findIndex((i) => i.name === item.name) === index
  );

  const updateCommonFilters = (values: any[]) => {
    // Filter out chart filter values
    const filteredValues = values.filter(
      (value) => !chartFilterValue?.find((chartFilter: any) => chartFilter.name === value.name)
    );

    // Update common filters with new values
    dispatch(
      updateCommonFiltersAction({
        filter: {
          type: FilterType.AUTOCOMPLETE,
          key: property,
          label: label,
          filter: { type: FilterType.AUTOCOMPLETE, label: label, value: filteredValues },
        },
        datasetId,
      })
    );
  };

  const removeItem = ({ newValue, removedItem }: { newValue: any[]; removedItem: any }) => {
    if (filterValue.find((value: any) => value.name === removedItem.name)) {
      updateCommonFilters(newValue);
    }

    if (chartFilterValue?.find((value: any) => value.name === removedItem.name)) {
      const filterIndex = chartFilters.findIndex(
        (chartFilter) => chartFilter.value === removedItem.name
      );

      if (filterIndex > -1) {
        dispatch(
          removeChartFilter({
            index: filterIndex,
            datasetId,
            propertyName: chartFilters[filterIndex]?.propertyName,
            value: chartFilters[filterIndex]?.value,
          })
        );
      }
    }
  };

  const handleChange = (event: MultiSelectChangeEvent) => {
    const value = event.value;

    if (event.value.length === 0) {
      dispatch(removeCommonFilter({ datasetId, filterKey: property }));

      if (chartFilterValue?.length > 0) {
        const chartFilterIndex = chartFilters.findIndex(
          (chartFilter) => chartFilter.propertyName === property
        );

        if (chartFilterIndex > -1) {
          dispatch(
            removeChartFilter({
              index: chartFilterIndex,
              datasetId,
              propertyName: chartFilters[chartFilterIndex]?.propertyName,
              value: chartFilters[chartFilterIndex]?.value,
            })
          );
        }
      }
      return;
    }

    if (event.value.length > composedFilterValue.length) {
      updateCommonFilters(value);
    }

    if (event.value.length < composedFilterValue.length) {
      const removedItem = composedFilterValue.find(
        (item: any) => !value.find((v: any) => v.name === item.name)
      );

      removeItem({ newValue: value, removedItem });
    }
  };

  useEffect(() => {
    setData(fullData?.filter(({ name }: AutocompleteFilterOption) => name !== undefined));
  }, [fullData]);

  const filterChange = (event: MultiSelectFilterChangeEvent) => {
    setData(filterBy(fullData?.slice() ?? [], event.filter) as any);
  };

  return (
    <div
      ref={popupContainerRef}
      className={`form-row autocomplete-filter ${!filterValid ? 'invalid' : ''}`}
    >
      <MultiSelect
        popupSettings={{
          appendTo: popupContainerRef.current,
          positionMode: 'absolute',
          offset: { left: 0, top: 0 },
          popupClass: 'autocomplete-popup',
        }}
        value={composedFilterValue}
        id={`filter-${formatElementId(label)}`}
        itemRender={(_, props) => {
          const index = composedFilterValue.findIndex(
            (fv: any) => fv.value === props.dataItem.value || fv.name === props.dataItem.name
          );
          const additionalClass = index > -1 ? ' k-selected' : '';

          return (
            <li
              id="option-:r21:-0"
              role="option"
              aria-selected="true"
              className={'k-list-item ' + additionalClass}
              style={{ position: 'unset', borderRadius: 10 }}
              onClick={(e: any) => (props.onClick ? props.onClick(props.index, e) : null)}
            >
              <span className="k-list-item-text ">
                {props.dataItem && props.dataItem.name ? props.dataItem.name : ''}
              </span>
            </li>
          );
        }}
        loading={isLoading || isFetching}
        label={label}
        data={dropdownData}
        textField={'name'}
        filterable={true}
        onChange={handleChange}
        onFilterChange={filterChange}
        placeholder="Please select ..."
        fillMode={'outline'}
        autoClose={property !== 'Survey'}
      />
      {!filterValid && (
        <div className="invalid-feedback">
          {validationMessage ?? DEFAULT_FILTER_VALIDATION_MESSAGE}
        </div>
      )}
    </div>
  );
}
